import { useState, useEffect, useRef } from 'react'
import { ShareSheetScreen, useShareSheetContext } from '../ShareSheetV2'
import { FileAccessGrant } from 'types'
import Styles from './Access.module.scss'
import { Button } from 'components/Button'
import { Add } from 'almanac-ui/icons'
import { RecipientAvatars } from '../RecipientAvatars/RecipientAvatars'
import useShareSheetV2 from 'hooks/useShareSheetV2'
import { InformalFeedbackRequest } from 'api/clients/InformalFeedbackRequestsClient'
import { useCurrentUserProfile } from 'hooks/queries/users/useCurrentUserProfile'
import { useAutopilotUpgradeGuard } from 'hooks/useAutopilotUpgradeGuard'

interface Props {
  accesses: FileAccessGrant[]
  request?: InformalFeedbackRequest
}

export const AccessListWithButton = ({ accesses, request }: Props) => {
  const { setScreen, file } = useShareSheetContext()
  const { setShareSheet } = useShareSheetV2(file)
  const [maxAccessors, setMaxAccessors] = useState(9)
  const containerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const user = useCurrentUserProfile().data

  const currentUser = {
    avatar_url: user?.profile_image_url || '',
    display_name: user?.display_name || '',
  }
  const recipientUsers = accesses.map((access) => {
    return {
      avatar_url: access.accessor.avatar_url,
      display_name: access.accessor.name,
    }
  })

  useEffect(() => {
    const currentContainer = containerRef.current

    const calculateMaxAccessors = () => {
      if (currentContainer) {
        const buttonWidth = buttonRef.current?.offsetWidth || 0
        const availableWidth = currentContainer.offsetWidth - buttonWidth
        const accessorWidth = 44

        const maxAccessors = Math.ceil(availableWidth / accessorWidth)
        setMaxAccessors(Math.max(0, maxAccessors))
      }
    }

    calculateMaxAccessors()

    const resizeObserver = new ResizeObserver((entries) => {
      calculateMaxAccessors()
    })

    if (currentContainer) {
      resizeObserver.observe(currentContainer)
    }

    return () => {
      if (currentContainer) {
        resizeObserver.unobserve(currentContainer)
      }
    }
  }, [accesses.length])

  const guardedAutopilotUpgradePress = useAutopilotUpgradeGuard(() => {
    setShareSheet({
      reviewScreenTitle: 'Invite people to review your work',
    })
    setScreen(ShareSheetScreen.InviteAccess)
  })

  return (
    <div ref={containerRef} className={Styles.accessList}>
      {recipientUsers.length > 0 && <RecipientAvatars users={recipientUsers} maxUsers={maxAccessors} />}
      {!recipientUsers.length && currentUser.display_name && <RecipientAvatars users={[currentUser]} />}
      <Button ref={buttonRef} className={Styles.inviteButton} onPress={guardedAutopilotUpgradePress}>
        <Add />
        {!!request ? 'Share' : 'Share for Review'}
      </Button>
    </div>
  )
}
